/**
 * FinOrchest - Unified Payments Service
 * 
 * This service provides a unified interface for all payment operations,
 * working with the new modular backend architecture.
 * 
 * Replaces the old RazorpayX-specific API calls.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Helper function to make authenticated API calls
 */
const apiCall = async (endpoint, options = {}) => {
  const response = await fetch(`${API_URL}/api/payments${endpoint}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.detail || 'API request failed');
  }
  
  return data;
};

/**
 * Provider Management
 */
export const providerAPI = {
  /**
   * Get current provider status
   */
  getStatus: () => apiCall('/provider/status'),
  
  /**
   * Get list of available providers
   */
  listProviders: async () => {
    const status = await apiCall('/provider/status');
    return status.available_providers || [];
  },
};

/**
 * Balance Operations
 */
export const balanceAPI = {
  /**
   * Get account balance
   * @param {string} accountId - Optional specific account ID
   */
  getBalance: (accountId = null) => {
    const params = accountId ? `?account_id=${accountId}` : '';
    return apiCall(`/balance${params}`);
  },
};

/**
 * Payment Operations
 */
export const paymentAPI = {
  /**
   * Initiate a new payment
   * @param {Object} paymentData - Payment details
   * @param {number} paymentData.amount - Amount in rupees
   * @param {string} paymentData.recipient_name - Recipient's name
   * @param {string} paymentData.account_number - Bank account number (optional)
   * @param {string} paymentData.ifsc_code - IFSC code (optional)
   * @param {string} paymentData.upi_id - UPI ID (optional)
   * @param {string} paymentData.mode - Payment mode (NEFT/RTGS/IMPS/UPI)
   * @param {string} paymentData.purpose - Purpose of payment
   * @param {string} paymentData.narration - Narration/notes
   * @param {string} paymentData.department_id - Department ID
   */
  initiate: (paymentData) => apiCall('/initiate', {
    method: 'POST',
    body: JSON.stringify(paymentData),
  }),
  
  /**
   * Get payment status
   * @param {string} paymentId - Payment ID
   */
  getStatus: (paymentId) => apiCall(`/status/${paymentId}`),
  
  /**
   * Get payment history
   * @param {Object} params - Filter parameters
   * @param {string} params.department_id - Filter by department
   * @param {string} params.status - Filter by status
   * @param {number} params.limit - Max results
   * @param {number} params.skip - Offset for pagination
   */
  getHistory: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.department_id) queryParams.append('department_id', params.department_id);
    if (params.status) queryParams.append('status', params.status);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    
    const queryString = queryParams.toString();
    return apiCall(`/history${queryString ? `?${queryString}` : ''}`);
  },
};

/**
 * Virtual Account Operations
 */
export const virtualAccountAPI = {
  /**
   * Create a virtual account for a department
   * @param {Object} data - Virtual account data
   * @param {string} data.department_id - Department ID
   * @param {string} data.department_name - Department name
   * @param {string} data.description - Description
   */
  create: (data) => apiCall('/virtual-accounts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  /**
   * List all virtual accounts
   * @param {string} departmentId - Optional filter by department
   */
  list: (departmentId = null) => {
    const params = departmentId ? `?department_id=${departmentId}` : '';
    return apiCall(`/virtual-accounts${params}`);
  },
  
  /**
   * Get virtual account for a specific department
   * @param {string} departmentId - Department ID
   */
  getByDepartment: async (departmentId) => {
    try {
      const accounts = await apiCall(`/virtual-accounts?department_id=${departmentId}`);
      return accounts.length > 0 ? { exists: true, virtual_account: accounts[0] } : { exists: false };
    } catch (err) {
      return { exists: false, error: err.message };
    }
  },
};

/**
 * CBDC Operations (Future - when CBDC is enabled)
 */
export const cbdcAPI = {
  /**
   * Allocate programmable budget to a department
   */
  allocateBudget: (departmentId, amount, rules) => apiCall('/cbdc/allocate-budget', {
    method: 'POST',
    body: JSON.stringify({ department_id: departmentId, amount, rules }),
  }),
  
  /**
   * Get wallet spending rules
   */
  getWalletRules: (walletId) => apiCall(`/cbdc/wallet-rules/${walletId}`),
};

/**
 * Utility Functions
 */
export const formatCurrency = (amount, options = {}) => {
  const currency = options.currency || 'INR';
  const locale = options.locale || 'en-IN';
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: options.decimals ?? 2,
  }).format(amount);
};

export const formatINR = (amount) => formatCurrency(amount, { currency: 'INR', locale: 'en-IN' });

/**
 * Payment mode labels
 */
export const PAYMENT_MODES = {
  NEFT: { label: 'NEFT', description: 'National Electronic Funds Transfer (1-2 hours)' },
  RTGS: { label: 'RTGS', description: 'Real Time Gross Settlement (Instant, min ₹2 Lakh)' },
  IMPS: { label: 'IMPS', description: 'Immediate Payment Service (Instant, 24/7)' },
  UPI: { label: 'UPI', description: 'Unified Payments Interface (Instant)' },
  CBDC: { label: 'Digital Rupee', description: 'Central Bank Digital Currency (Future)' },
};

/**
 * Provider type labels
 */
export const PROVIDER_LABELS = {
  mock: { name: 'Demo Mode', description: 'Simulated banking for testing & demos' },
  yes_bank: { name: 'Yes Bank', description: 'Direct integration with Yes Bank' },
  idfc_first: { name: 'IDFC First', description: 'Direct integration with IDFC First Bank' },
  federal_bank: { name: 'Federal Bank', description: 'Direct integration with Federal Bank' },
  cbdc: { name: 'Digital Rupee', description: 'RBI CBDC / Digital Rupee integration' },
  razorpayx: { name: 'RazorpayX (Legacy)', description: 'Deprecated - Use direct bank integration' },
};

export default {
  provider: providerAPI,
  balance: balanceAPI,
  payment: paymentAPI,
  virtualAccount: virtualAccountAPI,
  cbdc: cbdcAPI,
  formatCurrency,
  formatINR,
  PAYMENT_MODES,
  PROVIDER_LABELS,
};
